import type { AlertProps as HeroAlertRootProps } from "@heroui/react";
import { CloseButton, cn, Alert as HeroAlert } from "@heroui/react";
import { type ComponentProps, type ReactNode, useState } from "react";
import type { SlotsToClasses } from "src/utils/componentUtils";
import type { SemanticStatus } from "src/utils/themeUtils";
import {
  ALERT_ICONS,
  FOREGROUND_CLASSNAMES,
  SOFT_COLOR_CLASSNAMES,
} from "./alertConstants";

/** `primary` is brand emphasis, not a severity, so Alert extends the shared scale with it. */
export type AlertStatus = SemanticStatus | "primary";

/** `soft` — semantic tint from v3 soft tokens; `neutral` — neutral surface with only semantic icon and title color. */
export type AlertVariant = "soft" | "neutral";

export type AlertProps = Omit<HeroAlertRootProps, "status" | "children"> & {
  status?: AlertStatus;
  variant?: AlertVariant;
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  classNames?: SlotsToClasses<
    | "base"
    | "title"
    | "description"
    | "iconWrapper"
    | "mainWrapper"
    | "closeButton"
  >;
  endContent?: ReactNode;
  startContent?: ReactNode;
  isClosable?: boolean;
  onClose?: () => void;
  closeButtonProps?: ComponentProps<typeof CloseButton>;
  children?: ReactNode;
};

export function Alert({
  status = "default",
  variant = "soft",
  title,
  description,
  icon,
  className,
  classNames,
  endContent,
  startContent,
  isClosable,
  onClose,
  closeButtonProps,
  children,
  ...rest
}: AlertProps) {
  const [isVisible, setIsVisible] = useState(true);

  if (isClosable && !isVisible) {
    return null;
  }

  const heroStatus = STATUS_MAP[status] ?? "default";
  const softColorClasses =
    variant === "soft" ? SOFT_COLOR_CLASSNAMES[status] : undefined;
  const foregroundClasses = FOREGROUND_CLASSNAMES[status];
  const Icon = ALERT_ICONS[status];

  function handleClose() {
    setIsVisible(false);
    onClose?.();
  }

  return (
    <HeroAlert
      status={heroStatus}
      className={cn(
        "rounded-md",
        softColorClasses?.base,
        description == null && "items-center",
        className,
        classNames?.base,
      )}
      {...rest}
    >
      {startContent}
      <HeroAlert.Indicator
        className={cn(
          foregroundClasses?.indicator,
          softColorClasses?.indicator,
          classNames?.iconWrapper,
        )}
      >
        {icon ?? <Icon size={18} aria-hidden />}
      </HeroAlert.Indicator>
      <HeroAlert.Content
        className={cn(
          foregroundClasses?.content,
          softColorClasses?.content,
          classNames?.mainWrapper,
        )}
      >
        {title != null && (
          <HeroAlert.Title
            className={cn(
              foregroundClasses?.title,
              softColorClasses?.title,
              classNames?.title,
            )}
          >
            {title}
          </HeroAlert.Title>
        )}
        {description != null && (
          <HeroAlert.Description
            className={cn(
              softColorClasses?.description,
              classNames?.description,
            )}
          >
            {description}
          </HeroAlert.Description>
        )}
        {title == null && description == null && children}
      </HeroAlert.Content>
      {endContent}
      {isClosable && (
        <CloseButton
          {...closeButtonProps}
          className={cn(classNames?.closeButton, closeButtonProps?.className)}
          onPress={handleClose}
        />
      )}
    </HeroAlert>
  );
}

/** DS statuses that diverge from the HeroUI v3 status of the same name; the rest fall back to "default". */
const STATUS_MAP: Partial<Record<AlertStatus, HeroAlertRootProps["status"]>> = {
  primary: "accent",
  success: "success",
  warning: "warning",
  danger: "danger",
  critical: "danger",
  info: "default",
};
